import React, { useState, useMemo } from "react";
import { makeStyles, Typography, Button, useMediaQuery } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import * as Yup from "yup";
import Link from "next/link";
import { pushRemovedFromCart } from "lib/gtm";

import useCart from "hooks/useCart";
import GTMPushFunnelStep from "components/gtmTag/pushFunnelStep";
import ProductWithWarranty from "./productItem/ProductWithWarranty";
import FunnelCartRecap from "./summaryCard/FunnelCartSummary";
import BackgroundPoints from "../../assets/backgroundPoints.svg";
import BackgroundPointsLarge from "../../assets/backgroundPointsLarge.svg";
import FunnelForm from "../../components/FunnelForm/FunnelForm";
import { STEP_CART } from "../../constants";
import { hasCartNewAndRefurbishedProducts } from "../../utils";
import { FunnelModal } from "ui/funnelModal/FunnelModal";
const cartSchema = Yup.object().shape({
  coupon: Yup.string().min(1, "Le code promo est trop court").max(50, "Le code promo est trop long"),
});

export default function CartStepPanier() {
  const classes = useStyle();
  const [cart, dispatch] = useCart();
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down("sm"));
  const [submitFormik, setSubmitFormik] = useState(null);
  const gtmProducts = useMemo(() => cart.selection.map((s) => s.product), [cart.selection]);

  const deleteProduct = (product) => {
    pushRemovedFromCart(product, "basket_page");
    dispatch({ type: "remove", product });
  };

  return (
    <div className={classes.container}>
      <GTMPushFunnelStep step="1" products={gtmProducts} />
      <BackgroundPoints className={classes.shape} />
      {cart.selection.length !== 0 && !isTablet && <BackgroundPointsLarge className={classes.shapeBottom} />}
      <FunnelForm
        validationSchema={cartSchema}
        initialValues={{
          delivery_option_type: cart.shipping.delivery_option_type || "",
          recoverOldDevice: cart.shipping.recoverOldDevice || false,
        }}
        submitAction={(values) => {
          dispatch({ type: "updateShipping", shipping: { ...values } });
        }}
        step={STEP_CART}
        setSubmitFormik={setSubmitFormik}
      >
        <Typography color="secondary" variant="h3" className={classes.header}>
          {cart.selection.length > 0
            ? "Votre électroménager durable, sélectionné pour que ça dure"
            : "Votre panier est vide"}
        </Typography>
        <div className={classes.productList}>
          {cart.selection.map(({ product, warranty_pk }) => (
            <ProductWithWarranty
              key={product.slug}
              product={product}
              onDelete={() => deleteProduct(product)}
              warranty_pk={warranty_pk}
            />
          ))}
          {cart.selection.length === 0 && (
            <div className={classes.orderButton__container}>
              <Link href="/reconditionne" passHref>
                <Button variant="contained" color="primary" className={classes.orderButton}>
                  Retrouvez nos produits
                </Button>
              </Link>
            </div>
          )}
        </div>
      </FunnelForm>
      <FunnelModal
        open={hasCartNewAndRefurbishedProducts(cart)}
        size="sm"
        title={
          <Typography variant="h3" color="secondary">
            Les deux appareils choisis ne peuvent pas être livrés en même temps, veuillez procéder à deux commandes
            séparées ou contacter notre service client.
          </Typography>
        }
      ></FunnelModal>
      <div>
        <FunnelCartRecap cart={cart} />
        {!hasCartNewAndRefurbishedProducts(cart) && (
          <Button
            fullWidth
            variant="contained"
            color="primary"
            onClick={() => submitFormik()}
            disabled={cart.selection.length === 0}
            className={classes.orderButtonRight}
          >
            Choisir la livraison
          </Button>
        )}
      </div>
    </div>
  );
}

const useStyle = makeStyles((theme) => ({
  container: {
    // necessary for Lottie circle animations
    overflowX: "hidden",
    display: "grid",
    gridTemplateColumns: "2fr 1fr",
    gridColumnGap: theme.spacing(8),
    gridRowGap: theme.spacing(2),
    paddingBottom: theme.spacing(4),
    marginTop: theme.spacing(6),
    paddingRight: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
      marginTop: theme.spacing(2),
    },
  },
  productList: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gridRowGap: theme.spacing(4),
  },
  shape: {
    position: "absolute",
    left: 0,
    width: "calc(50vw - 480px)",
    top: 0,
    zIndex: -1,
  },
  shapeBottom: {
    position: "absolute",
    right: 0,
    width: "calc(50vw - 180px)",
    bottom: 0,
    zIndex: -1,
  },
  header: {
    gridColumn: "1/3",
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      gridColumn: "1",
    },
  },
  orderButton__container: {
    margin: `${theme.spacing(4)}px 0`,
  },
  orderButton: {
    [theme.breakpoints.down("sm")]: {
      marginBottom: theme.spacing(4),
    },
  },
}));
